import { useEffect, useState } from "react";
import { Plus, FileDown, Trash2, Loader2, Wrench, X, Pencil } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { pdfUrl } from "../../lib/pdfUrl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import ImageUploadField from "../../components/ImageUploadField";
import SignedPdfCell from "../../components/SignedPdfCell";
import ReportSuccessPanel from "../../components/ReportSuccessPanel";
import { useAuth } from "../../context/AuthContext";

const CHECKLIST = [
  "Dcp Cylinder & Main Valve",
  "Nitrogen Cartridge (16-18 Cu.in)",
  "Automatic & Manual Actuator",
  "Manual Operate Actuator",
  "Main Valve Pneumatic Actuator",
  "Vent Check",
  "Non Return Valve",
  "Nozzle 1/2",
  "Nozzle 1/4",
  "Control Unit",
  "Main Harness",
  "Sp Adaptor",
  "Fire Sensor Wire per",
  "Hose Kit",
  "Ms Part :",
];

const CONDITION_OPTIONS = ["", "Good", "Replaced", "Needs Repair", "Damaged", "Missing"];
const blankPart = { part_no: "", description: "", qty: "", action: "" };

const empty = {
  customer_name: "", site_name: "", area: "", project: "", po_no_date: "",
  eqpt_model: "", eqpt_sl_no: "",
  technician_name: "", date: "", product_installed: "",
  component_status: {},
  complain_date: "", complain_hmr: "", closed_date: "", closed_hmr: "",
  problem_found: "", work_done: "", spare_used: "",
  system_working_condition: "",
  failed_parts: [],
  symptoms: "",
  status_of_system: "",
  photo_image: "",
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-900 mb-3 pb-1 border-b border-slate-200">
      {title}
    </h3>
    {children}
  </div>
);

// Module-scope Field — stable identity → input never loses focus while typing.
const Field = ({ label, value, onChange, required, type = "text", placeholder, rows, testId }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider font-semibold mb-1 block">
      {label}{required && " *"}
    </Label>
    {rows ? (
      <Textarea rows={rows} required={required} value={value} onChange={onChange}
        placeholder={placeholder} className="rounded-none resize-y" data-testid={testId} />
    ) : (
      <Input type={type} required={required} value={value} onChange={onChange}
        placeholder={placeholder} className="rounded-none h-10" data-testid={testId} />
    )}
  </div>
);

export default function ServiceReportsAdmin() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/reports/service").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setStatus = (part, cond) => setForm({ ...form, component_status: { ...form.component_status, [part]: cond } });

  const openNew = () => {
    setCreated(null);
    setEditingId(null);
    setForm({
      ...empty,
      technician_name: user?.name || "",
      date: new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };

  const openEdit = (r) => {
    setCreated(null);
    setEditingId(r.id);
    setForm({
      customer_name: r.customer_name || "",
      site_name: r.site_name || "",
      area: r.area || "",
      project: r.project || "",
      po_no_date: r.po_no_date || "",
      eqpt_model: r.eqpt_model || "",
      eqpt_sl_no: r.eqpt_sl_no || "",
      technician_name: r.technician_name || "",
      date: r.date || "",
      product_installed: r.product_installed || "",
      component_status: r.component_status || {},
      complain_date: r.complain_date || "",
      complain_hmr: r.complain_hmr || "",
      closed_date: r.closed_date || "",
      closed_hmr: r.closed_hmr || "",
      problem_found: r.problem_found || "",
      work_done: r.work_done || "",
      spare_used: r.spare_used || "",
      system_working_condition: r.system_working_condition || "",
      failed_parts: r.failed_parts || [],
      symptoms: r.symptoms || "",
      status_of_system: r.status_of_system || "",
      photo_image: r.photo_image || "",
    });
    setOpen(true);
  };

  const addPart = () => setForm({ ...form, failed_parts: [...form.failed_parts, { ...blankPart }] });
  const updatePart = (i, patch) => {
    const fp = [...form.failed_parts];
    fp[i] = { ...fp[i], ...patch };
    setForm({ ...form, failed_parts: fp });
  };
  const removePart = (i) => setForm({ ...form, failed_parts: form.failed_parts.filter((_, j) => j !== i) });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingId) {
        const { data } = await api.put(`/reports/service/${editingId}`, form);
        toast.success(`Report ${data.report_no} updated`);
        setCreated(data); load();
      } else {
        const { data } = await api.post("/reports/service", form);
        toast.success(`Report ${data.report_no} created`);
        setCreated(data); load();
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const closeDialog = () => { setOpen(false); setCreated(null); setEditingId(null); load(); };

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.report_no}?`)) return;
    if (user.role !== "admin") return toast.error("Admin only");
    try {
      await api.delete(`/reports/service/${r.id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-service-reports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">Field Reports</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-3">
            <Wrench className="w-6 h-6 text-amber-500" /> General Service Reports
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Single-page form for fast field entry — checklist, failed parts, sign-off.
          </p>
        </div>
        <Button data-testid="new-service-report-btn" onClick={openNew} className="rounded-none btn-amber font-semibold h-10">
          <Plus className="w-4 h-4 mr-2" /> New Report
        </Button>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3 font-semibold">Report No</th>
              <th className="text-left p-3 font-semibold">Customer</th>
              <th className="text-left p-3 font-semibold">Equipment</th>
              <th className="text-left p-3 font-semibold">Technician</th>
              <th className="text-left p-3 font-semibold">Date</th>
              <th className="text-left p-3 font-semibold">Signed PDF</th>
              <th className="text-right p-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-200">
                <td className="p-3 font-mono text-xs">{r.report_no}</td>
                <td className="p-3 font-semibold">{r.customer_name}<div className="text-xs text-slate-500 font-normal">{r.site_name}</div></td>
                <td className="p-3">{r.eqpt_model || r.product_installed}<div className="text-xs text-slate-500">{r.eqpt_sl_no}</div></td>
                <td className="p-3">{r.technician_name}</td>
                <td className="p-3 font-mono text-xs">{r.date}</td>
                <td className="p-3"><SignedPdfCell report={r} endpoint="/reports/service" onUploaded={load} /></td>
                <td className="p-3 text-right space-x-1">
                  {r.pdf_url && (
                    <a href={pdfUrl(r)} target="_blank" rel="noopener noreferrer"
                       className="inline-flex p-2 hover:bg-slate-100" title="Download PDF">
                      <FileDown className="w-4 h-4" />
                    </a>
                  )}
                  <button
                    onClick={() => openEdit(r)}
                    className="inline-flex p-2 hover:bg-amber-50 text-amber-700"
                    title="Edit & regenerate PDF"
                    data-testid={`edit-service-${r.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {user.role === "admin" && (
                    <button onClick={() => del(r)} className="p-2 hover:bg-red-50 text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-slate-500 text-sm">No service reports yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); else setOpen(true); }}>
        <DialogContent className="max-w-4xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {created ? "Service Report Saved" : (editingId ? "Edit General Service Report" : "New General Service Report")}
            </DialogTitle>
          </DialogHeader>
          {created ? (
            <ReportSuccessPanel report={created} endpoint="/reports/service" onDone={closeDialog} onUploaded={load} />
          ) : (
          <form onSubmit={submit} className="mt-2" data-testid="service-report-form">
            <Section title="Customer & Site">
              <div className="grid md:grid-cols-2 gap-4">
                <Field value={form.customer_name} onChange={setField("customer_name")} label="Customer Name" required />
                <Field value={form.site_name} onChange={setField("site_name")} label="Site Name" required />
                <Field value={form.area} onChange={setField("area")} label="Area" placeholder="e.g. Bailadila Deposit-5" />
                <Field value={form.project} onChange={setField("project")} label="Project" placeholder="AFDSS Maintenance Contract" />
                <Field value={form.po_no_date} onChange={setField("po_no_date")} label="P.O. No. / Date" />
                <Field value={form.date} onChange={setField("date")} label="Service Date" type="date" required />
              </div>
            </Section>

            <Section title="Equipment">
              <div className="grid md:grid-cols-2 gap-4">
                <Field value={form.eqpt_model} onChange={setField("eqpt_model")} label="Equipment Model" placeholder="Komatsu HD465-7" />
                <Field value={form.eqpt_sl_no} onChange={setField("eqpt_sl_no")} label="Equipment SI No." placeholder="BLD-2284" />
                <Field value={form.product_installed} onChange={setField("product_installed")} label="Product of Service" required placeholder="AFDSS" />
                <Field value={form.technician_name} onChange={setField("technician_name")} label="Technician / Engineer" required />
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Field value={form.complain_date} onChange={setField("complain_date")} label="Customer Complain Date" type="date" />
                <Field value={form.closed_date} onChange={setField("closed_date")} label="Complain Closed Date" type="date" />
              </div>
            </Section>

            <Section title="UEE Parts Checklist">
              <p className="text-xs text-slate-500 mb-3">Tap each row to set the condition. Blank = not applicable.</p>
              <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-white text-xs uppercase">
                    <tr>
                      <th className="p-2 text-left">UEE Part</th>
                      <th className="p-2 text-left w-56">Condition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CHECKLIST.map((p) => (
                      <tr key={p} className="border-t border-slate-200">
                        <td className="p-2 font-medium">{p}</td>
                        <td className="p-1">
                          <select value={form.component_status[p] || ""}
                            onChange={(e) => setStatus(p, e.target.value)}
                            className="w-full rounded-none h-10 border border-slate-300 px-2 text-sm bg-white">
                            {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4">
                <Field value={form.system_working_condition} onChange={setField("system_working_condition")} label="System Working Condition" rows={3}
                  placeholder="System functional after maintenance. Power OK LED glowing." />
              </div>
            </Section>

            <Section title="Failed Parts">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-500">List each failed part with action taken.</p>
                <Button type="button" onClick={addPart} variant="outline" className="rounded-none h-8" data-testid="add-failed-part-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add row
                </Button>
              </div>
              <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-white text-xs uppercase">
                    <tr>
                      <th className="p-2 w-10">#</th>
                      <th className="p-2 text-left w-32">UEE Part No.</th>
                      <th className="p-2 text-left">Description</th>
                      <th className="p-2 w-20">Qty</th>
                      <th className="p-2 text-left w-44">Action</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.failed_parts.length === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-slate-500">
                        No failed parts. Click "Add row" if needed.
                      </td></tr>
                    )}
                    {form.failed_parts.map((p, i) => (
                      <tr key={i} className="border-t border-slate-200">
                        <td className="p-2 font-mono text-xs text-center">{i + 1}</td>
                        <td className="p-1"><Input value={p.part_no} onChange={(e) => updatePart(i, { part_no: e.target.value })} className="rounded-none h-9" /></td>
                        <td className="p-1"><Input value={p.description} onChange={(e) => updatePart(i, { description: e.target.value })} className="rounded-none h-9" /></td>
                        <td className="p-1"><Input value={p.qty} onChange={(e) => updatePart(i, { qty: e.target.value })} className="rounded-none h-9 text-right" /></td>
                        <td className="p-1">
                          <select value={p.action} onChange={(e) => updatePart(i, { action: e.target.value })}
                            className="w-full rounded-none h-9 border border-slate-300 px-2 text-sm bg-white">
                            <option value="">—</option><option>Repair</option><option>Reject</option><option>To Be Replaced</option>
                          </select>
                        </td>
                        <td className="p-1 text-right"><button type="button" onClick={() => removePart(i)} className="p-1 text-red-600 hover:bg-red-50"><X className="w-3 h-3" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Field value={form.problem_found} onChange={setField("problem_found")} label="Problem Found" rows={3} placeholder="Brief description" />
                <Field value={form.work_done} onChange={setField("work_done")} label="Work Done" rows={3} placeholder="Brief description" />
              </div>
              <div className="mt-4"><Field value={form.spare_used} onChange={setField("spare_used")} label="Spare Used" /></div>
            </Section>

            <Section title="Symptoms & Sign-off">
              <Field value={form.symptoms} onChange={setField("symptoms")} label="Symptoms" rows={3} placeholder="Low pressure on control unit display..." />
              <div className="mt-4">
                <Label className="text-xs uppercase tracking-wider font-semibold mb-2 block">Status of System</Label>
                <div className="flex flex-wrap gap-6 text-sm">
                  {[
                    { v: "under_warranty", l: "Under Warranty" },
                    { v: "out_of_warranty", l: "Out of Warranty" },
                    { v: "", l: "Not specified" },
                  ].map((opt) => (
                    <label key={opt.v} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="warranty" value={opt.v}
                        checked={form.status_of_system === opt.v}
                        onChange={() => setForm({ ...form, status_of_system: opt.v })}
                        className="accent-amber-500" />
                      <span>{opt.l}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="mt-4">
                <ImageUploadField label="Site Photo" value={form.photo_image}
                  onChange={(v) => setForm({ ...form, photo_image: v })} testId="svc-photo" />
              </div>
              <div className="mt-4 bg-amber-50 border border-amber-200 p-4 text-xs text-amber-800">
                <strong>Signatures:</strong> After generating the PDF, print it, collect Field Engineer &amp;
                Customer signatures, scan and upload using the <em>Upload Signed PDF</em> button.
              </div>
            </Section>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <Button type="button" variant="outline" onClick={closeDialog} className="rounded-none">Cancel</Button>
              <Button data-testid="svc-submit" type="submit" disabled={busy} className="rounded-none btn-amber font-semibold">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingId ? "Save & Regenerate PDF" : "Submit & Generate PDF"}
              </Button>
            </div>
          </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
